import { useState, useEffect } from "react";
import { useNavigate, NavLink, Link, Routes, Route, Navigate } from "react-router-dom";
import { Compass, ListChecks, BarChart3, Clapperboard, NotebookPen, CalendarDays, GalleryHorizontalEnd, Palette, Eye, Send, Users, Radar, BookMarked, Brain, LogOut, Menu, X, PanelsTopLeft, CreditCard, Zap } from "lucide-react";
import { Logo, Tag } from "../components/kit";
import { useAuth } from "../context/AuthContext";
import { openCookieSettings } from "../lib/consent";
import { toast } from "sonner";
import api from "../lib/api";
import { MailWarning } from "lucide-react";

const VerifyEmailBanner = () => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified !== false) return null;
  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox (and spam).");
    } catch (err) {
      toast.error(err.response?.status === 429 ? "Too many attempts — try again in a few minutes." : "Couldn't send — try again later.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap bg-[#FFF6E5] border-b border-[#F0E3C6] px-5 py-2.5" data-testid="verify-email-banner">
      <MailWarning size={15} strokeWidth={1.8} className="text-[#9A7B2F] shrink-0" />
      <p className="text-[13px] text-[#6E5A28] flex-1 min-w-[200px]">Verify your email to secure your account — check your inbox.</p>
      <button onClick={resend} disabled={busy}
              className="text-[12.5px] font-semibold text-[#181621] underline underline-offset-2 disabled:opacity-50"
              data-testid="resend-verification-btn">
        {busy ? "Sending…" : "Resend email"}
      </button>
    </div>
  );
};
import Strategy from "./Strategy";
import ActionPlan from "./ActionPlan";
import ScriptLab from "./ScriptLab";
import EditorialPlan from "./EditorialPlan";
import CalendarPage from "./CalendarPage";
import Panel from "./Panel";
import CreativeStudio from "./CreativeStudio";
import AdsSpy from "./AdsSpy";
import TrendRadar from "./TrendRadar";
import CarouselMaker from "./CarouselMaker";
import LandingPages from "./LandingPages";
import LandingEditor from "./LandingEditor";
import ReferenceLab from "./ReferenceLab";
import BrandBrainSettings from "./BrandBrainSettings";
import BillingPage from "./BillingPage";

const NAV_GROUPS = [
  {
    label: "CEO DASHBOARD",
    items: [
      { icon: Compass, label: "Strategy", path: "/app/ceo/strategy" },
      { icon: ListChecks, label: "Action Plan", path: "/app/ceo/action-plan" },
      { icon: BarChart3, label: "Results", soon: true },
    ],
  },
  {
    label: "ORGANIC STUDIO",
    items: [
      { icon: Clapperboard, label: "Scripts", path: "/app/organic/scripts" },
      { icon: NotebookPen, label: "Editorial Plan", path: "/app/organic/plan" },
      { icon: CalendarDays, label: "Calendar", path: "/app/organic/calendar" },
      { icon: GalleryHorizontalEnd, label: "Carousel Maker", path: "/app/organic/carousel" },
    ],
  },
  {
    label: "PERFORMANCE STUDIO",
    items: [
      { icon: Palette, label: "Creative Studio", path: "/app/performance/creative" },
      { icon: Eye, label: "Ads Spy", path: "/app/performance/ads-spy" },
      { icon: PanelsTopLeft, label: "Landing Pages", path: "/app/performance/landing" },
      { icon: Send, label: "Ads Publisher", soon: true },
    ],
  },
  {
    label: "BRAINSTORMING AREA",
    items: [
      { icon: Users, label: "Audience Test", path: "/app/brainstorm/audience-test" },
      { icon: Radar, label: "Trend Radar", path: "/app/brainstorm/trends" },
      { icon: BookMarked, label: "Reference Lab", path: "/app/brainstorm/reference-lab" },
    ],
  },
];

const SETTINGS_ITEMS = [
  { icon: Brain, label: "Brand Brain", path: "/app/settings/brand-brain" },
  { icon: CreditCard, label: "Plans & Credits", path: "/app/billing" },
];

const CreditsWidget = () => {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get("/billing/status").then((r) => setSt(r.data)).catch(() => {});
  }, []);
  if (!st || !st.plan) return null;
  const pct = Math.round((st.credits_left / Math.max(1, st.credits_total)) * 100);
  return (
    <button onClick={() => navigate("/app/billing")} className="w-full text-left mb-3 rounded-xl bg-[#F4F2EC] px-3.5 py-3 hover:bg-[#EFEDE6] transition-colors" data-testid="credits-widget">
      <div className="flex items-center justify-between">
        <span className="font-mono-j text-[9px] tracking-[0.14em] text-[#54505B] uppercase">{st.plan}{st.plan_status === "trial" ? " · TRIAL" : ""}</span>
        <span className="text-[11px] font-bold text-[#181621]" data-testid="credits-counter">{st.credits_left}/{st.credits_total}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-[#EAE5DB] overflow-hidden">
        <div className="h-full rounded-full bg-[#181621]" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1.5 text-[10.5px] text-[#9A9591]">credits · resets in {st.days_to_reset}d</p>
    </button>
  );
};

const BillingBanners = () => {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  useEffect(() => {
    api.get("/billing/status").then((r) => setSt(r.data)).catch(() => {});
    const onUpgrade = () => setUpgradeOpen(true);
    window.addEventListener("agenzee:upgrade", onUpgrade);
    return () => window.removeEventListener("agenzee:upgrade", onUpgrade);
  }, []);
  const lowCredits = st && st.plan && st.credits_left / Math.max(1, st.credits_total) < 0.15 && st.credits_left > 0;
  const trialEnding = st && st.plan_status === "trial" && st.trial_days_left !== null && st.trial_days_left <= 2;
  return (
    <>
      {lowCredits && (
        <div className="flex items-center gap-3 flex-wrap bg-[#FBEFE7] border-b border-[#EFD9C8] px-5 py-2.5" data-testid="low-credits-banner">
          <Zap size={15} strokeWidth={1.8} className="text-[#A65B2A] shrink-0" />
          <p className="text-[13px] text-[#7C4A24] flex-1 min-w-[200px]">You're running low on credits ({st.credits_left} left): upgrade to Pro for 3x the volume.</p>
          <button onClick={() => navigate("/app/billing")} className="text-[12.5px] font-semibold text-[#181621] underline underline-offset-2" data-testid="low-credits-upgrade-btn">Upgrade</button>
        </div>
      )}
      {trialEnding && (
        <div className="flex items-center gap-3 flex-wrap bg-[#EEF0FA] border-b border-[#D9DDF0] px-5 py-2.5" data-testid="trial-ending-banner">
          <Zap size={15} strokeWidth={1.8} className="text-[#4A56A6] shrink-0" />
          <p className="text-[13px] text-[#3B4489] flex-1 min-w-[200px]">Your trial ends in {st.trial_days_left} {st.trial_days_left === 1 ? "day" : "days"}: choose a plan to keep going.</p>
          <button onClick={() => navigate("/app/billing")} className="text-[12.5px] font-semibold text-[#181621] underline underline-offset-2" data-testid="trial-ending-cta">Choose a plan</button>
        </div>
      )}
      {upgradeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="upgrade-modal" onClick={() => setUpgradeOpen(false)}>
          <div className="card-soft bg-white p-7 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono-j text-[10px] tracking-[0.14em] text-[#54505B] uppercase">CREDITS EXHAUSTED</p>
            <h3 className="mt-3 text-xl font-bold text-[#181621]">Unlock more volume with Pro</h3>
            <p className="mt-2 text-[13.5px] text-[#54505B]">You've used up this cycle's credits. Pro gives you 2,500 credits/month (≈250 images) — or wait for the monthly reset.</p>
            <div className="mt-5 flex gap-2.5">
              <button onClick={() => { setUpgradeOpen(false); navigate("/app/billing"); }} className="flex-1 bg-[#181621] text-[#FAF8F3] text-[13px] font-semibold rounded-full py-2.5" data-testid="upgrade-modal-cta">See the plans</button>
              <button onClick={() => setUpgradeOpen(false)} className="px-4 text-[13px] text-[#54505B]" data-testid="upgrade-modal-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const NavItem = ({ item, onClose }) =>
  item.soon ? (
    <button
      disabled
      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[13px] font-medium text-[#B9B7B0] cursor-not-allowed"
      data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
    >
      <span className="flex items-center gap-2.5">
        <item.icon size={16} strokeWidth={1.8} />
        {item.label}
      </span>
      <Tag color="neutral">SOON</Tag>
    </button>
  ) : (
    <NavLink
      to={item.path}
      end
      onClick={onClose}
      className={({ isActive }) =>
        `w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
          isActive ? "bg-[#F5D6C6] text-[#181621]" : "text-[#54505B] hover:text-[#181621] hover:bg-[#FAF8F3]"
        }`
      }
      data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
    >
      <item.icon size={16} strokeWidth={1.8} />
      {item.label}
    </NavLink>
  );

const Sidebar = ({ onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-5 pt-6 pb-4 flex items-center justify-between shrink-0">
        <Logo className="h-5" />
        {onClose && (
          <button onClick={onClose} className="lg:hidden text-[#54505B]" data-testid="sidebar-close-btn">
            <X size={20} strokeWidth={1.8} />
          </button>
        )}
      </div>
      <nav className="px-3 mt-1 flex-1 overflow-y-auto pb-4">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="mt-4 first:mt-0" data-testid={`sidebar-group-${group.label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="font-mono-j text-[9px] tracking-[0.16em] text-[#9A9591] uppercase px-3 mb-1">{group.label}</p>
            <div className="space-y-0.5">
              {group.items.map((item) => <NavItem key={item.label} item={item} onClose={onClose} />)}
            </div>
          </div>
        ))}
        <div className="mt-5 pt-4 border-t border-[#F3EFE7]" data-testid="sidebar-group-settings">
          <p className="font-mono-j text-[9px] tracking-[0.16em] text-[#9A9591] uppercase px-3 mb-1">SETTINGS</p>
          <div className="space-y-0.5">
            {SETTINGS_ITEMS.map((item) => <NavItem key={item.label} item={item} onClose={onClose} />)}
          </div>
        </div>
      </nav>
      <div className="px-5 py-4 border-t border-[#F3EFE7] shrink-0">
        <CreditsWidget />
        <p className="text-[12.5px] font-semibold text-[#181621] truncate" data-testid="sidebar-user-name">{user?.name || user?.email}</p>
        <p className="text-[11.5px] text-[#9A9591] truncate">{user?.email}</p>
        <button
          className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#54505B] hover:text-[#181621] transition-colors"
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          data-testid="sidebar-logout-btn"
        >
          <LogOut size={14} strokeWidth={1.8} /> Log out
        </button>
        <div className="mt-3 flex flex-wrap gap-x-1.5 gap-y-0.5 text-[10.5px] text-[#9A9591]" data-testid="sidebar-legal-links">
          <Link to="/privacy" className="hover:text-[#181621]">Privacy</Link>
          <span>·</span>
          <Link to="/termini" className="hover:text-[#181621]">Terms</Link>
          <span>·</span>
          <Link to="/cookie" className="hover:text-[#181621]">Cookie</Link>
          <span>·</span>
          <button onClick={openCookieSettings} className="hover:text-[#181621]" data-testid="sidebar-cookie-settings-btn">Cookie settings</button>
        </div>
      </div>
    </div>
  );
};

export default function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-screen dot-grid lg:flex" data-testid="app-shell">
      <aside className="hidden lg:block w-60 shrink-0 h-screen sticky top-0 shadow-[0_12px_32px_rgba(24,22,33,0.06)]">
        <Sidebar />
      </aside>

      <div className="lg:hidden flex items-center justify-between px-5 py-4 bg-white shadow-[0_4px_16px_rgba(24,22,33,0.05)]">
        <Logo className="h-5" />
        <button onClick={() => setMenuOpen(true)} className="text-[#181621]" data-testid="mobile-menu-btn">
          <Menu size={22} strokeWidth={1.8} />
        </button>
      </div>
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-[#181621]/30" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 shadow-2xl">
            <Sidebar onClose={() => setMenuOpen(false)} />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <VerifyEmailBanner />
        <BillingBanners />
        <Routes>
          <Route index element={<Navigate to="ceo/strategy" replace />} />
          <Route path="ceo/strategy" element={<Strategy />} />
          <Route path="ceo/action-plan" element={<ActionPlan />} />
          <Route path="organic/scripts" element={<ScriptLab />} />
          <Route path="organic/plan" element={<EditorialPlan />} />
          <Route path="organic/calendar" element={<CalendarPage />} />
          <Route path="organic/carousel" element={<CarouselMaker />} />
          <Route path="brainstorm/audience-test" element={<Panel />} />
          <Route path="brainstorm/panel" element={<Navigate to="/app/brainstorm/audience-test" replace />} />
          <Route path="brainstorm/trends" element={<TrendRadar />} />
          <Route path="brainstorm/reference-lab" element={<ReferenceLab />} />
          <Route path="performance/creative" element={<CreativeStudio />} />
          <Route path="performance/ads-spy" element={<AdsSpy />} />
          <Route path="performance/landing" element={<LandingPages />} />
          <Route path="performance/landing/:id" element={<LandingEditor />} />
          <Route path="settings/brand-brain" element={<BrandBrainSettings />} />
          <Route path="billing" element={<BillingPage />} />
          {/* legacy URL redirects */}
          <Route path="action-plan" element={<Navigate to="/app/ceo/action-plan" replace />} />
          <Route path="organic-studio" element={<Navigate to="/app/organic/scripts" replace />} />
          <Route path="script-lab" element={<Navigate to="/app/organic/scripts" replace />} />
          <Route path="panel" element={<Navigate to="/app/brainstorm/audience-test" replace />} />
          <Route path="*" element={<Navigate to="/app/ceo/strategy" replace />} />
        </Routes>
      </main>
    </div>
  );
}
